import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { EmptyState } from './EmptyState';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';
import ConfirmDialog from './ConfirmDialog';
import { formsApi, handleApiError, type EventForm } from '../services/api';
import { FileText, Plus, Edit2, Trash2, Eye, Users, Calendar, MoreVertical, Copy, Settings } from 'lucide-react';

interface FormsConfigurationProps {
  eventId: string;
}

interface FormData {
  title: string;
  description: string;
  isActive: boolean;
}

export const FormsConfiguration: React.FC<FormsConfigurationProps> = ({ eventId }) => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<EventForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingForm, setEditingForm] = useState<EventForm | null>(null);
  const [deletingForm, setDeletingForm] = useState<EventForm | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    isActive: true
  });

  // Load forms for the event
  const loadForms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await formsApi.getByEventId(eventId);
      setForms(response.forms);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, [eventId]);

  // Reset form data
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      isActive: true
    });
  };

  // Handle form input changes
  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle create form
  const handleCreateForm = async () => {
    try {
      setFormLoading(true);
      setError(null);

      const newFormData = {
        title: formData.title.trim(),
        description: formData.description.trim() || '',
        fields: [], // Start with empty fields - will be configured in form builder
        isActive: formData.isActive,
        submissionCount: 0,
        updatedAt: new Date()
      };

      const newForm = await formsApi.create(eventId, newFormData);
      setForms(prev => [...prev, newForm]);
      setShowCreateModal(false);
      resetForm();
      
      // Navigate to form builder for the new form
      navigate(`/admin/forms/${newForm.id}/builder`);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle edit form
  const handleEditForm = async () => {
    if (!editingForm) return;

    try {
      setFormLoading(true);
      setError(null);

      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim() || '',
        isActive: formData.isActive
      };

      const updatedForm = await formsApi.update(editingForm.id.toString(), updateData);
      setForms(prev => prev.map(form => form.id === editingForm.id ? updatedForm : form));
      setEditingForm(null);
      resetForm();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete form
  const handleDeleteForm = async () => {
    if (!deletingForm) return;

    try {
      setFormLoading(true);
      setError(null);

      await formsApi.delete(deletingForm.id.toString());
      setForms(prev => prev.filter(form => form.id !== deletingForm.id));
      setDeletingForm(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (form: EventForm) => {
    setEditingForm(form);
    setFormData({
      title: form.title,
      description: form.description,
      isActive: form.isActive
    });
  };

  // Close modals
  const closeModals = () => {
    setShowCreateModal(false);
    setEditingForm(null);
    resetForm();
    setError(null);
  };

  // Navigate to form builder
  const openFormBuilder = (formId: string) => {
    navigate(`/admin/forms/${formId}/builder`);
  };

  // Navigate to form preview
  const openFormPreview = (formId: string) => {
    navigate(`/admin/forms/${formId}/preview`);
  };

  // Handle duplicate form
  const handleDuplicateForm = async (form: EventForm) => {
    try {
      setFormLoading(true);
      setError(null);

      const duplicateData = {
        title: `${form.title} (Copy)`,
        description: form.description,
        fields: form.fields,
        isActive: false, // Set duplicate as inactive by default
        submissionCount: 0,
        updatedAt: new Date()
      };

      const newForm = await formsApi.create(eventId, duplicateData);
      setForms(prev => [...prev, newForm]);
      setOpenDropdown(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = (formId: string) => {
    setOpenDropdown(openDropdown === formId ? null : formId);
  };

  // Close dropdown when clicking outside
  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const isFormValid = formData.title.trim();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Forms</h3>
          <p className="text-sm text-gray-600">
            Create and manage custom forms for your event
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)} 
          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Form
        </Button>
      </div>

      {error && (
        <Alert type="error" title="Error" message={error} />
      )}

      {forms.length === 0 ? (
        <EmptyState
          title="No forms created"
          description="Create custom forms to collect additional information from participants."
          action={{
            label: "Create First Form",
            onClick: () => setShowCreateModal(true)
          }}
        />
      ) : (
        <div className="bg-white shadow overflow-visible sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {forms.map((form) => (
              <li key={form.id} className="px-6 py-4 relative overflow-visible">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{form.title}</h4>
                        {form.description && (
                          <p className="text-sm text-gray-600 mt-1">{form.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={form.isActive ? 'success' : 'default'}>
                          {form.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 mt-3 text-sm text-gray-500">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-1" />
                        Fields: {form.fields?.length || 0}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        Submissions: {form.submissionCount || 0}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Created: {new Date(form.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="relative ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleDropdown(form.id.toString())}
                      className="text-gray-600 hover:text-gray-700"
                      title="More actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>

                    {openDropdown === form.id.toString() && (
                      <>
                        {/* Backdrop to close dropdown */}
                        <div
                          className="fixed inset-0 z-30"
                          onClick={closeDropdown}
                        />
                        
                        {/* Dropdown menu */}
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-40">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                openFormPreview(form.id.toString());
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Eye className="w-4 h-4 mr-3" />
                              View
                            </button>
                            
                            <button
                              onClick={() => {
                                openFormBuilder(form.id.toString());
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Edit2 className="w-4 h-4 mr-3" />
                              Edit Fields
                            </button>
                            
                            <button
                              onClick={() => {
                                openEditModal(form);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Settings className="w-4 h-4 mr-3" />
                              Edit Form
                            </button>
                            
                            <button
                              onClick={() => {
                                handleDuplicateForm(form);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Copy className="w-4 h-4 mr-3" />
                              Duplicate
                            </button>
                            
                            <div className="border-t border-gray-100"></div>
                            
                            <button
                              onClick={() => {
                                setShowCreateModal(false);
                                setEditingForm(null);
                                setDeletingForm(form);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-3" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      <Modal
        isOpen={showCreateModal || !!editingForm}
        onClose={closeModals}
        title={editingForm ? 'Edit Form Details' : 'Create New Form'}
        size="lg"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Form Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Portfolio Submission Form"
            />
            {!formData.title.trim() && (
              <p className="text-red-500 text-sm mt-1">Form title is required</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of what this form is for..."
            />
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Form is active and available for submission</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="secondary" onClick={closeModals}>
              Cancel
            </Button>
            <Button
              onClick={editingForm ? handleEditForm : handleCreateForm}
              disabled={!isFormValid || formLoading}
              loading={formLoading}
            >
              {editingForm ? 'Update Form' : 'Create Form'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deletingForm}
        onClose={() => setDeletingForm(null)}
        onConfirm={handleDeleteForm}
        title="Delete Form"
        message={`Are you sure you want to delete "${deletingForm?.title}"? This action cannot be undone and will remove all form submissions.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={formLoading}
      />
    </div>
  );
};

export default FormsConfiguration;
